import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { ChevronLeft, ChevronRight, RefreshCw, Target } from "lucide-react";
import { Button } from "@/components/ui/button";

const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function buildGrid(year, month) {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startWeekday = first.getDay();
    const total = last.getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
}

export default function CalendarPage() {
    const { user } = useAuth();
    const cur = user?.currency || "USD";
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth()); // 0-11
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDay, setSelectedDay] = useState(null);

    useEffect(() => {
        setLoading(true);
        api.get(`/calendar/events?year=${year}&month=${month + 1}`)
            .then(({ data }) => setEvents(data))
            .finally(() => setLoading(false));
    }, [year, month]);

    const eventsByDay = useMemo(() => {
        const map = {};
        events.forEach(e => {
            const d = parseInt(e.date.slice(8, 10), 10);
            map[d] = map[d] || [];
            map[d].push(e);
        });
        return map;
    }, [events]);

    const grid = useMemo(() => buildGrid(year, month), [year, month]);

    const prev = () => {
        let m = month - 1, y = year;
        if (m < 0) { m = 11; y -= 1; }
        setMonth(m); setYear(y); setSelectedDay(null);
    };
    const next = () => {
        let m = month + 1, y = year;
        if (m > 11) { m = 0; y += 1; }
        setMonth(m); setYear(y); setSelectedDay(null);
    };

    const isTodayCell = (d) => d && today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;
    const dayEvents = selectedDay ? (eventsByDay[selectedDay] || []) : events;

    return (
        <div className="space-y-4" data-testid="calendar-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Calendar</h1>
                    <p className="text-sm text-slate-500 mt-1">Subscription renewals & savings deadlines</p>
                </div>
                <div className="flex items-center gap-2 card-elev rounded-2xl p-1.5">
                    <button onClick={prev} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
                    <span className="font-display font-bold text-slate-900 px-2 min-w-[140px] text-center" data-testid="cal-title">{MONTHS[month]} {year}</span>
                    <button onClick={next} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Calendar grid */}
                <div className="card-elev rounded-2xl p-5 lg:col-span-2" data-testid="cal-grid">
                    <div className="grid grid-cols-7 gap-1 text-center mb-2">
                        {DAYS.map(d => <div key={d} className="text-[11px] font-bold uppercase tracking-wider text-slate-400 py-1">{d}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                        {grid.map((d, i) => {
                            if (d === null) return <div key={i} className="aspect-square" />;
                            const evs = eventsByDay[d] || [];
                            const isSel = selectedDay === d;
                            const isToday = isTodayCell(d);
                            return (
                                <button
                                    key={i} data-testid={`day-${d}`}
                                    onClick={() => setSelectedDay(isSel ? null : d)}
                                    className={`aspect-square rounded-xl p-1.5 text-left flex flex-col items-start justify-start transition-all
                                        ${isSel ? "bg-indigo-600 text-white" :
                                          isToday ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200" :
                                          "hover:bg-slate-50 text-slate-700"}`}
                                >
                                    <span className={`text-xs font-semibold ${isSel ? "text-white" : ""}`}>{d}</span>
                                    {evs.length > 0 ? (
                                        <div className="mt-auto flex gap-1 flex-wrap">
                                            {evs.slice(0, 3).map(e => (
                                                <span key={e.id} className={`w-1.5 h-1.5 rounded-full ${isSel ? "bg-white/80" : ""}`} style={!isSel ? { backgroundColor: e.color } : {}} />
                                            ))}
                                            {evs.length > 3 ? <span className="text-[10px] font-semibold opacity-70">+{evs.length - 3}</span> : null}
                                        </div>
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Agenda */}
                <div className="card-elev rounded-2xl p-5" data-testid="cal-agenda">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-lg font-bold text-slate-900">
                            {selectedDay ? `${MONTHS[month]} ${selectedDay}` : "All this month"}
                        </h3>
                        {selectedDay ? <Button variant="ghost" onClick={() => setSelectedDay(null)} className="text-xs">Clear</Button> : null}
                    </div>
                    {loading ? (
                        <div className="text-center text-slate-400 text-sm py-8">Loading…</div>
                    ) : dayEvents.length === 0 ? (
                        <div className="text-center py-10 text-sm text-slate-500">Nothing scheduled.</div>
                    ) : (
                        <div className="space-y-2.5">
                            {dayEvents.map(e => (
                                <div key={e.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50">
                                    <span className="w-9 h-9 rounded-xl grid place-items-center text-white" style={{ backgroundColor: e.color }}>
                                        {e.type === "subscription" ? <RefreshCw className="w-4 h-4" /> : <Target className="w-4 h-4" />}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm text-slate-900 truncate">{e.title}</div>
                                        <div className="text-xs text-slate-500">{fmtDate(e.date)} · {e.type === "subscription" ? "Renewal" : "Goal due"}</div>
                                    </div>
                                    <div className="font-display font-bold text-sm text-slate-900 tabular-nums">{fmtCurrency(e.amount, cur)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
